/**
 * MultiEntityChart — "Two brands. Twelve restaurants. Thirty subsidiaries."
 * Four ascending horizontal bars: Masterbeef Group's disclosed structure at
 * its April 2025 Nasdaq listing (424B4 + F-1 Exhibit 21.1, SEC EDGAR).
 * Bars animate on load; each carries a hover/tap tooltip with the verbatim
 * source language. Requires React 16.8+. No props; injects scoped styles once.
 */
'use client';

import React, { useEffect } from 'react';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* -------------------------------- Data --------------------------------- */
interface Row { label: string; value: number; display: string; color: string; tip: string; }
const MAX = 30;
const ROWS: Row[] = [
  { label: 'Brands', value: 2, display: '2', color: '#a3e8e0',
    tip: '<b>2 brands</b><br>&ldquo;Master Beef and Anping Grill brands&rdquo;<br><span class="t-src">424B4 prospectus, Apr 2025</span>' },
  { label: 'Restaurants', value: 12, display: '12', color: '#2db5ad',
    tip: '<b>12 restaurants</b><br>&ldquo;we operate 12 restaurant outlets&rdquo;<br><span class="t-src">424B4 prospectus, as of Apr 2025</span>' },
  { label: 'HK operating subsidiaries', value: 20, display: '≈20', color: '#0f9993',
    tip: '<b>&asymp;20 HK operating subsidiaries</b><br>&ldquo;around 20 operating subsidiaries<br>operating in Hong Kong&rdquo;<br><span class="t-src">424B4 prospectus, verbatim</span>' },
  { label: 'Subsidiaries in total', value: 30, display: '30', color: '#0d555c',
    tip: '<b>30 subsidiaries in total</b><br>23 Hong Kong + 6 BVI holding<br>companies + 1 Taiwan<br><span class="t-src">F-1 Exhibit 21.1</span>' },
];

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'mec-styles';
const CSS = `
.mec-root { --mec-font: 'Poppins', system-ui, -apple-system, sans-serif; --mec-mono: var(--mec-font);
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3;
  --line: #E4E7EA; --line-2: #EEF1F2; --paper: #FFFFFF; --wash: #F5F8F8;
  --teal: #0f9993; --teal-deep: #0d555c; --gold: #B08A2E; --gold-deep: #7A5F1D;
  position: relative; box-sizing: border-box; width: 100%; max-width: 860px;
  background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35);
  padding: 40px 44px 28px; color: var(--ink);
  font-family: var(--mec-font); -webkit-font-smoothing: antialiased; }
.mec-root .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
.mec-root .mec-logo { height: 40px; width: auto; flex: none; }
.mec-root.compact .mec-logo { display: none; }
.mec-root *, .mec-root *::before, .mec-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.mec-root h1, .mec-root h2, .mec-root h3, .mec-root p, .mec-root img { margin: 0; }
.mec-root p { letter-spacing: normal; }
.mec-root h1 { font-size: 29px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.3; max-width: 560px; }
.mec-root h1 .gold { color: var(--gold); display: block; }
.mec-root .sub { font-size: 14px; color: var(--body); margin-top: 10px; max-width: 620px; line-height: 1.55; }
.mec-root .chart { margin-top: 30px; position: relative; display: flex; flex-direction: column; gap: 18px; }
.mec-root .row { display: grid; grid-template-columns: 208px 1fr; align-items: center; gap: 16px; }
.mec-root .lbl { font-size: 14px; font-weight: 500; color: var(--ink-2); text-align: right; line-height: 1.3; }
.mec-root .track { position: relative; height: 34px; background: var(--wash); border-radius: 7px; cursor: pointer; }
.mec-root .bar { position: absolute; inset: 0 auto 0 0; width: 0; border-radius: 7px; transition: width 1.1s cubic-bezier(0.19,1,0.22,1); }
.mec-root .val { position: absolute; top: 50%; transform: translateY(-50%); left: calc(var(--w) + 12px); font-size: 19px; font-weight: 600; font-family: var(--mec-mono); color: var(--ink); opacity: 0; transition: opacity .5s ease; white-space: nowrap; }
.mec-root.drawn .val { opacity: 1; }
.mec-root .ref { position: absolute; top: -8px; bottom: -8px; border-left: 1.5px dashed var(--gold); opacity: 0; transition: opacity .6s ease .9s; }
.mec-root.drawn .ref { opacity: 1; }
.mec-root .anno { margin-top: 4px; display: grid; grid-template-columns: 208px 1fr; gap: 16px; opacity: 0; transition: opacity .6s ease 1s; }
.mec-root.drawn .anno { opacity: 1; }
.mec-root .anno p { grid-column: 2; font-size: 13.5px; font-weight: 600; color: var(--gold-deep); line-height: 1.5; }
.mec-root .anno p span { font-weight: 500; color: var(--gold); }
.mec-root .tip { position: absolute; pointer-events: none; background: var(--ink); color: #fff; padding: 10px 13px; border-radius: 10px; font-size: 12.5px; line-height: 1.55; opacity: 0; transform: translate(-50%, calc(-100% - 14px)); transition: opacity .15s; white-space: nowrap; box-shadow: 0 12px 30px -12px rgba(0,0,0,0.5); z-index: 5; }
.mec-root .tip b { font-weight: 600; }
.mec-root .tip .t-src { color: #A9D9D4; font-family: var(--mec-mono); font-size: 11px; }
.mec-root .tip::after { content: ''; position: absolute; left: 50%; bottom: -5px; transform: translateX(-50%) rotate(45deg); width: 10px; height: 10px; background: var(--ink); }
.mec-root .foot { margin-top: 28px; padding-top: 16px; border-top: 1px solid var(--line); }
.mec-root .foot .cap { font-size: 12.5px; line-height: 1.55; color: var(--body); font-style: italic; max-width: 680px; }
.mec-root .src { margin-top: 12px; font-family: var(--mec-mono); font-size: 10px; color: var(--muted); line-height: 1.6; }
.mec-root.compact { padding: 26px 0 20px; border-radius: 0; border-left: none; border-right: none; box-shadow: none; }
.mec-root.compact h1 { font-size: 22px; }
.mec-root.compact .sub { font-size: 13px; }
.mec-root.compact .chart { margin-top: 24px; gap: 20px; }
.mec-root.compact .row { grid-template-columns: 1fr; gap: 7px; }
.mec-root.compact .lbl { text-align: left; font-size: 13px; }
.mec-root.compact .track { height: 30px; }
.mec-root.compact .val { font-size: 17px; }
.mec-root.compact .anno { grid-template-columns: 1fr; margin-top: 0; }
.mec-root.compact .anno p { grid-column: 1; font-size: 13px; }`;

function useInjectedStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID; s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

function useInView(ref: React.RefObject<HTMLDivElement | null>): boolean {
  const [inView, setInView] = React.useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      const t = window.setTimeout(() => setInView(true), 0);
      return () => window.clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setInView(true); },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, inView]);
  return inView;
}

/* ------------------------------ Component ------------------------------ */
interface Tip { html: string; x: number; y: number; }
const SPAN = MAX / 0.88; // bars top out at 88% of the track so labels fit

const MultiEntityChart: React.FC = () => {
  useInjectedStyles();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const chartRef = React.useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef);
  const [compact, setCompact] = React.useState(false);
  const [drawn, setDrawn] = React.useState(false);
  // `tip` keeps the last tooltip's position/content so the fade-out happens in
  // place; clearing it on leave would jump the fading box to the corner.
  const [tip, setTip] = React.useState<Tip | null>(null);
  const [tipOpen, setTipOpen] = React.useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = (): void => setCompact(el.offsetWidth < 640);
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, []);

  useEffect(() => {
    if (!inView) return;
    const t = window.setTimeout(() => setDrawn(true), 60);
    return () => window.clearTimeout(t);
  }, [inView]);

  const pct = (v: number): string => `${((v / SPAN) * 100).toFixed(2)}%`;

  const showTip = (e: React.MouseEvent<HTMLDivElement>, r: Row): void => {
    const chart = chartRef.current;
    const track = e.currentTarget;
    if (!chart) return;
    const c = chart.getBoundingClientRect();
    const t = track.getBoundingClientRect();
    setTip({ html: r.tip, x: t.left - c.left + t.width * (r.value / SPAN) / 2, y: t.top - c.top });
    setTipOpen(true);
  };

  return (
    <div className={`mec-root${compact ? ' compact' : ''}${drawn ? ' drawn' : ''}`} ref={rootRef}>
      <div className="head">
        <h1>Two brands. Twelve restaurants. <span className="gold">Thirty subsidiaries.</span></h1>
        <img className="mec-logo" src={PSGLOBAL_LOGO} alt="PS Global Consulting" />
      </div>
      <p className="sub">MasterBeef Group&rsquo;s disclosed structure at its April 2025 Nasdaq listing &mdash; the standard shape of a Hong Kong F&amp;B group, at listed-company scale.</p>

      <div className="chart" ref={chartRef}>
        {ROWS.map((r, i) => (
          <div className="row" key={r.label}>
            <div className="lbl">{r.label}</div>
            <div className="track" style={{ ['--w' as string]: pct(r.value) }}
              onMouseEnter={(e) => showTip(e, r)} onMouseLeave={() => setTipOpen(false)} onClick={(e) => showTip(e, r)}>
              {i === ROWS.length - 1 && <div className="ref" style={{ left: pct(ROWS[1].value) }}></div>}
              <div className="bar" style={{ width: drawn ? pct(r.value) : 0, background: r.color, transitionDelay: `${i * 140}ms` }}></div>
              <div className="val" style={{ transitionDelay: `${i * 140 + 500}ms` }}>{r.display}</div>
            </div>
          </div>
        ))}
        <div className="anno">
          <p>2.5 companies per restaurant <span>&mdash; the corporate structure outnumbers the shops it runs</span></p>
        </div>
        <div className="tip" style={tip ? { left: tip.x, top: tip.y, opacity: tipOpen ? 1 : 0 } : undefined} dangerouslySetInnerHTML={{ __html: tip ? tip.html : '' }}></div>
      </div>

      <div className="foot">
        <p className="cap">The prospectus itself describes &ldquo;around 20 operating subsidiaries operating in Hong Kong&rdquo; &mdash; more companies than restaurants, before counting the holding companies above them.</p>
      </div>
      <p className="src">Source: MasterBeef Group 424B4 prospectus (Apr 2025) and F-1 Exhibit 21.1, SEC EDGAR. Counts as of the prospectus date.</p>
    </div>
  );
};

export default MultiEntityChart;
